import { Clock, FileText, Store } from "@tamagui/lucide-icons";
import { type ReactNode, useContext } from "react";
import { type GetProps, View, XStack, YStack } from "tamagui";

import type { CarePlanMedication } from "@/api/generated/models";
import RxIcon from "@/assets/icons/RXIcon";
import { getShadow, useGetColor } from "@/theme/helpers";
import { Button } from "./Button";
import { Header } from "./Layout";
import { LayoutContext } from "./LayoutProvider";
import Logo from "./Logo";
import { Heading, Paragraph, Text } from "./Typography";

const READY_COPY = "Usually ready in 1–2 hours";
const DEFAULT_BUTTON_LABEL = "View full care plan";
const ICON_SIZE = 16;

type PharmacyGroup = {
  pharmacy: CarePlanMedication["pharmacy"];
  medications: CarePlanMedication[];
};

export type CarePlanSummaryCardProps = {
  /** Header line — differs by surface (e.g. CHAI vs. clinic chat). */
  title: string;
  /** AI-generated episode-of-care title, shown as the subtitle. */
  eocTitle?: string | null;
  /** Prescriptions to summarise; drives the rx / pharmacy block. */
  medications?: CarePlanMedication[] | null;
  onViewCarePlan: () => void;
  buttonLabel?: string;
  dataTrackingId?: string;
} & Omit<GetProps<typeof XStack>, "children">;

// Group medications by pickup location so a care plan whose prescriptions are
// split across pharmacies shows each medication paired with its own pharmacy
// (mirrors the grouping in the full CarePlanDisplay).
const groupByPharmacy = (meds: CarePlanMedication[]): PharmacyGroup[] => [
  ...meds
    .reduce((groups, med) => {
      const key = med.pharmacy
        ? `${med.pharmacy.name}|${med.pharmacy.address}`
        : "no-pharmacy";
      const group = groups.get(key);
      if (group) {
        group.medications.push(med);
      } else {
        groups.set(key, { pharmacy: med.pharmacy ?? null, medications: [med] });
      }
      return groups;
    }, new Map<string, PharmacyGroup>())
    .values(),
];

const InfoRow = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) => (
  <XStack gap="$100" alignItems="flex-start">
    <View flexShrink={0} flexGrow={0} marginTop="$25">
      {icon}
    </View>
    <YStack flex={1} gap="$25">
      {children}
    </YStack>
  </XStack>
);

/**
 * Unified care plan card used across every surface (CHAI async + redirect,
 * clinic chat). Renders the "You visited Curai Health" template with the
 * optional prescription / pharmacy summary and a single "View full care plan"
 * call to action. Callers own opening the full care plan (via `onViewCarePlan`)
 * so this stays presentational and free of modal/navigation dependencies.
 */
export default function CarePlanSummaryCard({
  title,
  eocTitle,
  medications,
  onViewCarePlan,
  buttonLabel = DEFAULT_BUTTON_LABEL,
  dataTrackingId = "view-full-care-plan",
  ...rest
}: CarePlanSummaryCardProps) {
  const { lg } = useContext(LayoutContext);
  const mutedIconColor = useGetColor("$textSecondary");

  const meds = medications?.filter((med) => med?.name) ?? [];
  const hasRx = meds.length > 0;
  const pharmacyGroups = groupByPharmacy(meds);

  const rxLabel =
    meds.length === 1
      ? "Your prescription has been sent:"
      : "Your prescriptions have been sent:";

  const rxIcon = (
    <RxIcon
      color={mutedIconColor}
      backgroundColor="transparent"
      width={ICON_SIZE}
      height={ICON_SIZE}
    />
  );

  const button = (
    <Button
      mode="primary"
      icon={FileText}
      onPress={onViewCarePlan}
      dataTrackingId={dataTrackingId}
      alignSelf={lg ? "center" : "stretch"}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <XStack
      gap="$200"
      padding="$300"
      borderRadius="$150"
      backgroundColor="$fillCallout"
      {...getShadow()}
      {...rest}
    >
      <View flexShrink={0} flexGrow={0}>
        <Logo.Only />
      </View>
      <YStack flex={1} gap="$150" alignItems="flex-start">
        <Header>
          <Heading size="$md">{title}</Heading>
          {!!eocTitle && (
            <Paragraph size="$sm" color="$textSecondary">
              {eocTitle}
            </Paragraph>
          )}
        </Header>

        {hasRx && (
          <YStack gap="$150" alignSelf="stretch">
            <Paragraph size="$sm" color="$textSecondary">
              {rxLabel}
            </Paragraph>

            {pharmacyGroups.map((group, groupIdx) => (
              <YStack
                key={
                  group.pharmacy
                    ? `${group.pharmacy.name}|${group.pharmacy.address}`
                    : `no-pharmacy-${groupIdx}`
                }
                gap="$100"
              >
                <InfoRow icon={rxIcon}>
                  {group.medications.map((med, idx) => (
                    <Text key={`${med.name}_${idx}`} size="$sm">
                      {med.name}
                    </Text>
                  ))}
                </InfoRow>

                {group.pharmacy && (
                  <>
                    <InfoRow
                      icon={<Store size={ICON_SIZE} color="$textSecondary" />}
                    >
                      <Text size="$sm">{group.pharmacy.name}</Text>
                      <Text size="$sm" color="$textSecondary">
                        {group.pharmacy.address}
                      </Text>
                    </InfoRow>

                    <InfoRow
                      icon={<Clock size={ICON_SIZE} color="$textSecondary" />}
                    >
                      <Text size="$sm" color="$textSecondary">
                        {READY_COPY}
                      </Text>
                    </InfoRow>
                  </>
                )}
              </YStack>
            ))}
          </YStack>
        )}

        {!lg && button}
      </YStack>
      {lg && button}
    </XStack>
  );
}
